export type ReactionId =
  | 'me-gusta'
  | 'me-encanta'
  | 'esta-brutal'
  | 'gran-programa'
  | 'buena-musica'
  | 'excelente'
  | 'divertido'
  | 'sorprendente';

export type ContentTypeId =
  | 'posts'
  | 'fotos'
  | 'videos'
  | 'comentarios'
  | 'eventos'
  | 'concursos'
  | 'programas'
  | 'locutores';

export type MetricPeriod = 'hoy' | 'semana' | 'mes' | 'total';

export interface ReactionType {
  id: ReactionId;
  name: string;
  shortName: string;
  emoji: string;
  icon: string;
  description: string;
  color: string;
  gradient: string;
  bgColor: string;
  textColor: string;
  hoverBgColor: string;
  ringColor: string;
  glowColor: string;
  scoreValue: number;
  totalCount: number;
  rank: number;
  trend: 'up' | 'down' | 'stable';
  trendPercent: number;
}

export interface ContentTypeConfig {
  id: ContentTypeId;
  name: string;
  icon: string;
  description: string;
  color: string;
  bgColor: string;
  textColor: string;
  applicableReactions: ReactionId[];
  totalReactions: number;
}

export interface ContentItem {
  id: number;
  type: ContentTypeId;
  title: string;
  author: string;
  authorPhoto: string;
  radio: string;
  radioFreq: string;
  thumbnailUrl: string;
  excerpt: string;
  totalReactions: number;
  mainReaction: ReactionId;
  mainReactionPct: number;
  breakdown: Partial<Record<ReactionId, number>>;
  timeAgo: string;
  trending: boolean;
}

export interface UserReaction {
  id: number;
  userId: number;
  userName: string;
  userPhoto: string;
  contentId: number;
  contentType: ContentTypeId;
  reactionId: ReactionId;
  timeAgo: string;
}

export interface ReactionDashboardStats {
  totalReactions: number;
  reactionsToday: number;
  reactionsThisWeek: number;
  reactionsThisMonth: number;
  mainReaction: string;
  mainReactionCount: number;
  trendingReaction: string;
  trendingReactionPercent: number;
  totalScoreGenerated: number;
  avgReactionsPerUser: number;
  activeReactors: number;
}

export interface ScoreAction {
  id: string;
  label: string;
  description: string;
  points: number;
  icon: string;
  color: string;
}

export const reactionTypes: ReactionType[] = [
  {
    id: 'me-gusta',
    name: 'Me Gusta',
    shortName: 'GUSTA',
    emoji: '👍',
    icon: 'ri-thumb-up-line',
    description: 'El clásico. Simple, directo y universal. Decís "me gusta esto" sin complicaciones.',
    color: 'text-sky-600',
    gradient: 'from-sky-400 to-cyan-500',
    bgColor: 'bg-sky-100',
    textColor: 'text-sky-700',
    hoverBgColor: 'hover:bg-sky-200',
    ringColor: 'ring-sky-300/60',
    glowColor: 'shadow-sky-400/30',
    scoreValue: 1,
    totalCount: 2847000,
    rank: 1,
    trend: 'stable',
    trendPercent: 3.2,
  },
  {
    id: 'me-encanta',
    name: 'Me Encanta',
    shortName: 'ENCANTA',
    emoji: '❤️',
    icon: 'ri-heart-3-line',
    description: 'Cuando algo te llega al corazón. Más fuerte que un like — es amor genuino por el contenido.',
    color: 'text-rose-600',
    gradient: 'from-rose-400 to-pink-500',
    bgColor: 'bg-rose-100',
    textColor: 'text-rose-700',
    hoverBgColor: 'hover:bg-rose-200',
    ringColor: 'ring-rose-300/60',
    glowColor: 'shadow-rose-400/30',
    scoreValue: 3,
    totalCount: 1923000,
    rank: 2,
    trend: 'up',
    trendPercent: 8.5,
  },
  {
    id: 'esta-brutal',
    name: 'Está Brutal',
    shortName: 'BRUTAL',
    emoji: '🔥',
    icon: 'ri-fire-line',
    description: 'Cuando algo te vuela la cabeza. Lo máximo. Reservado para contenido que realmente rompe.',
    color: 'text-orange-600',
    gradient: 'from-orange-400 to-red-500',
    bgColor: 'bg-orange-100',
    textColor: 'text-orange-700',
    hoverBgColor: 'hover:bg-orange-200',
    ringColor: 'ring-orange-300/60',
    glowColor: 'shadow-orange-400/30',
    scoreValue: 5,
    totalCount: 1456000,
    rank: 3,
    trend: 'up',
    trendPercent: 12.3,
  },
  {
    id: 'gran-programa',
    name: 'Gran Programa',
    shortName: 'PROGRAMA',
    emoji: '🎙️',
    icon: 'ri-mic-line',
    description: 'Reconocimiento directo a un programa de radio. El locutor y equipo se lucieron.',
    color: 'text-indigo-600',
    gradient: 'from-indigo-400 to-blue-500',
    bgColor: 'bg-indigo-100',
    textColor: 'text-indigo-700',
    hoverBgColor: 'hover:bg-indigo-200',
    ringColor: 'ring-indigo-300/60',
    glowColor: 'shadow-indigo-400/30',
    scoreValue: 4,
    totalCount: 987000,
    rank: 4,
    trend: 'up',
    trendPercent: 5.7,
  },
  {
    id: 'buena-musica',
    name: 'Buena Música',
    shortName: 'MÚSICA',
    emoji: '🎵',
    icon: 'ri-music-line',
    description: 'La playlist, la canción, el momento musical. Cuando el sonido merece aplauso.',
    color: 'text-emerald-600',
    gradient: 'from-emerald-400 to-green-500',
    bgColor: 'bg-emerald-100',
    textColor: 'text-emerald-700',
    hoverBgColor: 'hover:bg-emerald-200',
    ringColor: 'ring-emerald-300/60',
    glowColor: 'shadow-emerald-400/30',
    scoreValue: 2,
    totalCount: 1120000,
    rank: 5,
    trend: 'up',
    trendPercent: 4.1,
  },
  {
    id: 'excelente',
    name: 'Excelente',
    shortName: 'EXCELENTE',
    emoji: '👏',
    icon: 'ri-hand-heart-line',
    description: 'Aplauso de pie. Calidad indiscutible. Para contenido que merece ovación.',
    color: 'text-amber-600',
    gradient: 'from-amber-400 to-yellow-500',
    bgColor: 'bg-amber-100',
    textColor: 'text-amber-700',
    hoverBgColor: 'hover:bg-amber-200',
    ringColor: 'ring-amber-300/60',
    glowColor: 'shadow-amber-400/30',
    scoreValue: 4,
    totalCount: 1340000,
    rank: 6,
    trend: 'up',
    trendPercent: 7.8,
  },
  {
    id: 'divertido',
    name: 'Divertido',
    shortName: 'DIVERTIDO',
    emoji: '😂',
    icon: 'ri-emotion-laugh-line',
    description: 'Te hizo reír a carcajadas. El humor es contagioso y esta reacción lo celebra.',
    color: 'text-yellow-600',
    gradient: 'from-yellow-400 to-amber-500',
    bgColor: 'bg-yellow-100',
    textColor: 'text-yellow-700',
    hoverBgColor: 'hover:bg-yellow-200',
    ringColor: 'ring-yellow-300/60',
    glowColor: 'shadow-yellow-400/30',
    scoreValue: 3,
    totalCount: 1780000,
    rank: 7,
    trend: 'up',
    trendPercent: 9.2,
  },
  {
    id: 'sorprendente',
    name: 'Sorprendente',
    shortName: 'SORPRENDE',
    emoji: '😮',
    icon: 'ri-emotion-line',
    description: 'No te lo esperabas. Te dejó con la boca abierta. Para momentos inesperados.',
    color: 'text-fuchsia-600',
    gradient: 'from-fuchsia-400 to-purple-500',
    bgColor: 'bg-fuchsia-100',
    textColor: 'text-fuchsia-700',
    hoverBgColor: 'hover:bg-fuchsia-200',
    ringColor: 'ring-fuchsia-300/60',
    glowColor: 'shadow-fuchsia-400/30',
    scoreValue: 3,
    totalCount: 895000,
    rank: 8,
    trend: 'up',
    trendPercent: 11.5,
  },
];

export const contentTypeConfigs: ContentTypeConfig[] = [
  {
    id: 'posts',
    name: 'Posts',
    icon: 'ri-article-line',
    description: 'Publicaciones, noticias y actualizaciones de las radios.',
    color: 'text-sky-600',
    bgColor: 'bg-sky-50',
    textColor: 'text-sky-700',
    applicableReactions: ['me-gusta', 'me-encanta', 'esta-brutal', 'excelente', 'divertido', 'sorprendente'],
    totalReactions: 2840000,
  },
  {
    id: 'fotos',
    name: 'Fotos',
    icon: 'ri-image-line',
    description: 'Galerías, momentos capturados y contenido visual.',
    color: 'text-rose-600',
    bgColor: 'bg-rose-50',
    textColor: 'text-rose-700',
    applicableReactions: ['me-gusta', 'me-encanta', 'esta-brutal', 'excelente', 'divertido', 'sorprendente'],
    totalReactions: 2150000,
  },
  {
    id: 'videos',
    name: 'Videos',
    icon: 'ri-video-line',
    description: 'Transmisiones grabadas, clips y contenido audiovisual.',
    color: 'text-orange-600',
    bgColor: 'bg-orange-50',
    textColor: 'text-orange-700',
    applicableReactions: ['me-gusta', 'me-encanta', 'esta-brutal', 'excelente', 'divertido', 'sorprendente'],
    totalReactions: 1980000,
  },
  {
    id: 'comentarios',
    name: 'Comentarios',
    icon: 'ri-chat-1-line',
    description: 'Interacciones en publicaciones, fotos, videos y cabina.',
    color: 'text-teal-600',
    bgColor: 'bg-teal-50',
    textColor: 'text-teal-700',
    applicableReactions: ['me-gusta', 'me-encanta', 'esta-brutal', 'excelente', 'divertido', 'sorprendente'],
    totalReactions: 1620000,
  },
  {
    id: 'eventos',
    name: 'Eventos',
    icon: 'ri-calendar-event-line',
    description: 'Eventos presenciales y virtuales organizados por las radios.',
    color: 'text-violet-600',
    bgColor: 'bg-violet-50',
    textColor: 'text-violet-700',
    applicableReactions: ['me-gusta', 'me-encanta', 'esta-brutal', 'excelente', 'sorprendente', 'divertido'],
    totalReactions: 980000,
  },
  {
    id: 'concursos',
    name: 'Concursos',
    icon: 'ri-trophy-line',
    description: 'Competencias, sorteos y desafíos de la comunidad.',
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
    textColor: 'text-amber-700',
    applicableReactions: ['me-gusta', 'me-encanta', 'esta-brutal', 'excelente', 'sorprendente'],
    totalReactions: 1250000,
  },
  {
    id: 'programas',
    name: 'Programas',
    icon: 'ri-broadcast-line',
    description: 'Programas de radio con sus episodios y contenido.',
    color: 'text-indigo-600',
    bgColor: 'bg-indigo-50',
    textColor: 'text-indigo-700',
    applicableReactions: ['me-gusta', 'me-encanta', 'esta-brutal', 'gran-programa', 'buena-musica', 'excelente', 'divertido', 'sorprendente'],
    totalReactions: 1760000,
  },
  {
    id: 'locutores',
    name: 'Locutores',
    icon: 'ri-user-voice-line',
    description: 'Perfiles de locutores con su trayectoria y habilidades.',
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50',
    textColor: 'text-emerald-700',
    applicableReactions: ['me-gusta', 'me-encanta', 'esta-brutal', 'gran-programa', 'excelente', 'divertido', 'sorprendente'],
    totalReactions: 1340000,
  },
];

export const topReactedContent: ContentItem[] = [
  {
    id: 1,
    type: 'posts',
    title: '¡Llegamos a 100K oyentes en vivo! Gracias a toda la comunidad RFW por hacer historia juntos esta noche.',
    author: 'Radio Élite Digital',
    authorPhoto: 'https://readdy.ai/api/search-image?query=Radio%20station%20logo%20modern%20circular%20minimalist%20design%20warm%20golden%20accents%20on%20dark%20background%20professional%20broadcasting%20emblem%20sleek%20contemporary&width=120&height=120&seq=rxn-content-elite&orientation=squarish',
    radio: 'Radio Élite Digital',
    radioFreq: '99.1 FM',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Massive%20crowd%20at%20a%20live%20radio%20broadcast%20event%20outdoor%20festival%20stage%20with%20lights%20colorful%20confetti%20in%20the%20air%20energetic%20celebration%20atmosphere%20wide%20shot%20editorial%20photography%20warm%20sunset%20lighting&width=800&height=450&seq=rxn-thumb-elite-celebration&orientation=landscape',
    excerpt: 'Una noche histórica para la radio digital. Más de 100 mil oyentes conectados simultáneamente en vivo.',
    totalReactions: 48700,
    mainReaction: 'esta-brutal',
    mainReactionPct: 38,
    breakdown: { 'esta-brutal': 18500, 'me-encanta': 12400, 'excelente': 8900, 'me-gusta': 5400, 'sorprendente': 3500 },
    timeAgo: 'Hace 3 horas',
    trending: true,
  },
  {
    id: 2,
    type: 'videos',
    title: 'El momento exacto en que Carlos Vives entró sorpresa a la cabina y cantó en vivo con nosotros.',
    author: 'Estéreo Capital',
    authorPhoto: 'https://readdy.ai/api/search-image?query=Radio%20station%20logo%20circular%20warm%20earthy%20tones%20professional%20broadcasting%20emblem%20contemporary%20minimalist%20design&width=120&height=120&seq=rxn-content-capital&orientation=squarish',
    radio: 'Estéreo Capital',
    radioFreq: '102.1 FM',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Exciting%20moment%20inside%20a%20radio%20studio%20a%20famous%20musician%20surprises%20the%20hosts%20live%20on%20air%20genuine%20shocked%20expressions%20warm%20studio%20lighting%20professional%20broadcast%20environment%20candid%20photography&width=800&height=450&seq=rxn-thumb-capital-surprise&orientation=landscape',
    excerpt: 'El artista colombiano apareció sin avisar durante el programa matutino y regaló una versión acústica inolvidable.',
    totalReactions: 42300,
    mainReaction: 'sorprendente',
    mainReactionPct: 42,
    breakdown: { 'sorprendente': 17700, 'esta-brutal': 10500, 'me-encanta': 8200, 'excelente': 3800, 'buena-musica': 2100 },
    timeAgo: 'Hace 8 horas',
    trending: true,
  },
  {
    id: 3,
    type: 'fotos',
    title: 'Backstage de la final de Copas RFW 2026 — los 4 equipos finalistas antes de la gran batalla.',
    author: 'RFW Oficial',
    authorPhoto: 'https://readdy.ai/api/search-image?query=Official%20RFW%20platform%20logo%20professional%20modern%20circular%20design%20white%20and%20gold%20accents%20confident%20branding%20emblem&width=120&height=120&seq=rxn-content-rfw&orientation=squarish',
    radio: 'RFW Oficial',
    radioFreq: 'Global',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Four%20teams%20of%20radio%20hosts%20backstage%20at%20a%20competition%20final%20event%20professional%20casual%20attire%20warm%20camaraderie%20moments%20group%20photo%20style%20editorial%20photography%20rich%20contrast%20lighting&width=800&height=450&seq=rxn-thumb-rfw-copas&orientation=landscape',
    excerpt: 'Los finalistas de las 4 categorías posan juntos antes de la competencia más esperada del año.',
    totalReactions: 39100,
    mainReaction: 'esta-brutal',
    mainReactionPct: 35,
    breakdown: { 'esta-brutal': 13700, 'excelente': 9800, 'me-encanta': 8200, 'me-gusta': 5100, 'divertido': 2300 },
    timeAgo: 'Hace 12 horas',
    trending: true,
  },
  {
    id: 4,
    type: 'programas',
    title: 'El Despertador — Edición Especial 500 episodios. 4 horas de los mejores momentos.',
    author: 'Radio Éxito FM',
    authorPhoto: 'https://readdy.ai/api/search-image?query=Radio%20station%20logo%20circular%20bright%20energetic%20warm%20orange%20and%20yellow%20tones%20professional%20broadcasting%20emblem%20contemporary%20design&width=120&height=120&seq=rxn-content-exito&orientation=squarish',
    radio: 'Radio Éxito FM',
    radioFreq: '98.5 FM',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Morning%20radio%20show%20hosts%20celebrating%20a%20milestone%20episode%20500%20cake%20decorations%20party%20atmosphere%20inside%20a%20radio%20studio%20warm%20joyful%20lighting%20balloons%20professional%20broadcast%20setting&width=800&height=450&seq=rxn-thumb-exito-500&orientation=landscape',
    excerpt: 'Medio millar de mañanas despertando a la ciudad. Los hosts comparten anécdotas, llamadas históricas y sorpresas.',
    totalReactions: 35600,
    mainReaction: 'gran-programa',
    mainReactionPct: 45,
    breakdown: { 'gran-programa': 16000, 'me-encanta': 8200, 'excelente': 5600, 'esta-brutal': 3500, 'divertido': 2300 },
    timeAgo: 'Hace 1 día',
    trending: true,
  },
  {
    id: 5,
    type: 'posts',
    title: 'Anunciamos el concurso "La Voz de tu Ciudad" — el ganador tendrá su propio programa en vivo.',
    author: 'Radio Juvenil FM',
    authorPhoto: 'https://readdy.ai/api/search-image?query=Radio%20station%20logo%20circular%20youthful%20energetic%20neon%20pink%20and%20cyan%20accents%20modern%20design%20professional%20broadcasting%20emblem&width=120&height=120&seq=rxn-content-juvenil&orientation=squarish',
    radio: 'Radio Juvenil FM',
    radioFreq: '105.3 FM',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Microphone%20on%20stage%20with%20spotlight%20dramatic%20lighting%20concert%20venue%20atmosphere%20talent%20show%20concept%20professional%20photography%20warm%20and%20cool%20contrasting%20lighting&width=800&height=450&seq=rxn-thumb-juvenil-voz&orientation=landscape',
    excerpt: 'La búsqueda del mejor talento vocal de la ciudad comienza. Inscripciones abiertas desde mañana.',
    totalReactions: 31200,
    mainReaction: 'me-encanta',
    mainReactionPct: 32,
    breakdown: { 'me-encanta': 10000, 'esta-brutal': 7800, 'me-gusta': 6200, 'excelente': 4200, 'sorprendente': 3000 },
    timeAgo: 'Hace 2 días',
    trending: true,
  },
  {
    id: 6,
    type: 'videos',
    title: 'Tutorial: Cómo armar tu propio estudio de radio en casa con menos de $200.',
    author: 'Radio Industria',
    authorPhoto: 'https://readdy.ai/api/search-image?query=Radio%20station%20logo%20circular%20industrial%20modern%20style%20metallic%20warm%20tones%20professional%20broadcasting%20emblem&width=120&height=120&seq=rxn-content-industria&orientation=squarish',
    radio: 'Radio Industria',
    radioFreq: '93.5 FM',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=DIY%20home%20radio%20studio%20setup%20with%20budget%20equipment%20microphone%20audio%20interface%20laptop%20on%20desk%20warm%20lighting%20creative%20workspace%20modern%20minimalist%20aesthetic%20clean%20organized&width=800&height=450&seq=rxn-thumb-industria-studio&orientation=landscape',
    excerpt: 'Guía completa paso a paso para empezar a transmitir con calidad profesional sin gastar una fortuna.',
    totalReactions: 28400,
    mainReaction: 'excelente',
    mainReactionPct: 40,
    breakdown: { 'excelente': 11400, 'me-encanta': 6500, 'me-gusta': 4800, 'sorprendente': 3200, 'esta-brutal': 2500 },
    timeAgo: 'Hace 3 días',
    trending: false,
  },
  {
    id: 7,
    type: 'eventos',
    title: 'RadioFest 2026 — El festival de radios más grande de Latinoamérica vuelve con 3 días de música en vivo.',
    author: 'RFW Oficial',
    authorPhoto: 'https://readdy.ai/api/search-image?query=Official%20RFW%20platform%20logo%20professional%20modern%20circular%20design%20white%20and%20gold%20accents%20confident%20branding%20emblem&width=120&height=120&seq=rxn-content-rfw2&orientation=squarish',
    radio: 'RFW Oficial',
    radioFreq: 'Global',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Massive%20outdoor%20music%20festival%20RadioFest%202026%20with%20multiple%20stages%20crowd%20enjoying%20live%20performances%20colorful%20lights%20evening%20atmosphere%20wide%20panoramic%20shot%20professional%20event%20photography&width=800&height=450&seq=rxn-thumb-rfw-fest&orientation=landscape',
    excerpt: '30 radios, 50 artistas, 3 escenarios. El evento que reúne a toda la comunidad radial en un solo lugar.',
    totalReactions: 27500,
    mainReaction: 'esta-brutal',
    mainReactionPct: 33,
    breakdown: { 'esta-brutal': 9100, 'me-encanta': 7200, 'excelente': 5100, 'sorprendente': 3500, 'me-gusta': 2600 },
    timeAgo: 'Hace 4 días',
    trending: false,
  },
  {
    id: 8,
    type: 'locutores',
    title: 'Perfil de Ricky Navarro — De pasante a la voz más escuchada de la mañana en solo 2 años.',
    author: 'Radio Élite Digital',
    authorPhoto: 'https://readdy.ai/api/search-image?query=Radio%20station%20logo%20modern%20circular%20minimalist%20design%20warm%20golden%20accents%20on%20dark%20background%20professional%20broadcasting%20emblem%20sleek%20contemporary&width=120&height=120&seq=rxn-content-elite2&orientation=squarish',
    radio: 'Radio Élite Digital',
    radioFreq: '99.1 FM',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Young%20charismatic%20male%20radio%20host%20in%20studio%20professional%20casual%20style%20warm%20smile%20headphones%20around%20neck%20professional%20broadcast%20environment%20golden%20hour%20lighting%20through%20window&width=800&height=450&seq=rxn-thumb-elite-ricky&orientation=landscape',
    excerpt: 'La historia de cómo un joven apasionado por la radio se convirtió en el locutor estrella de la emisora digital más grande.',
    totalReactions: 26100,
    mainReaction: 'excelente',
    mainReactionPct: 36,
    breakdown: { 'excelente': 9400, 'me-encanta': 6800, 'esta-brutal': 4500, 'gran-programa': 3200, 'me-gusta': 2200 },
    timeAgo: 'Hace 5 días',
    trending: false,
  },
  {
    id: 9,
    type: 'concursos',
    title: 'Ganadores del Concurso "Crea tu Jingles" — Escuchá los 3 temas ganadores que ya suenan al aire.',
    author: 'Radio Peninsular',
    authorPhoto: 'https://readdy.ai/api/search-image?query=Radio%20station%20logo%20circular%20coastal%20inspired%20blue%20and%20warm%20sand%20tones%20professional%20broadcasting%20emblem%20contemporary%20design&width=120&height=120&seq=rxn-content-peninsular&orientation=squarish',
    radio: 'Radio Peninsular',
    radioFreq: '101.9 FM',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Three%20winners%20holding%20prize%20envelopes%20on%20stage%20at%20a%20radio%20contest%20award%20ceremony%20confetti%20falling%20spotlights%20warm%20celebratory%20atmosphere%20professional%20event%20photography&width=800&height=450&seq=rxn-thumb-peninsular-jingles&orientation=landscape',
    excerpt: 'Más de 500 participantes enviaron sus creaciones. Estos son los 3 jingles que conquistaron al jurado y a la audiencia.',
    totalReactions: 24800,
    mainReaction: 'me-encanta',
    mainReactionPct: 30,
    breakdown: { 'me-encanta': 7400, 'excelente': 6200, 'buena-musica': 4800, 'esta-brutal': 3600, 'me-gusta': 2800 },
    timeAgo: 'Hace 6 días',
    trending: false,
  },
  {
    id: 10,
    type: 'fotos',
    title: 'Así se vivió la transmisión especial desde la cima del Cerro de la Silla — 360° de ciudad.',
    author: 'Radio Deportiva Total',
    authorPhoto: 'https://readdy.ai/api/search-image?query=Radio%20station%20logo%20circular%20sporty%20energetic%20red%20and%20black%20tones%20professional%20broadcasting%20emblem%20contemporary%20dynamic%20design&width=120&height=120&seq=rxn-content-deportiva&orientation=squarish',
    radio: 'Radio Deportiva Total',
    radioFreq: '95.7 FM',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Radio%20hosts%20broadcasting%20live%20from%20a%20mountain%20summit%20with%20panoramic%20city%20view%20below%20portable%20equipment%20breathtaking%20landscape%20golden%20late%20afternoon%20light%20adventure%20vibe%20professional%20photography&width=800&height=450&seq=rxn-thumb-deportiva-cerro&orientation=landscape',
    excerpt: 'El equipo subió 1,800 metros para transmitir el programa deportivo con la mejor vista de la ciudad.',
    totalReactions: 23200,
    mainReaction: 'esta-brutal',
    mainReactionPct: 37,
    breakdown: { 'esta-brutal': 8600, 'excelente': 5400, 'me-encanta': 4200, 'sorprendente': 3100, 'me-gusta': 1900 },
    timeAgo: 'Hace 7 días',
    trending: false,
  },
];

export const userReactions: UserReaction[] = [
  { id: 1, userId: 1, userName: 'Isabella Torres', userPhoto: 'https://readdy.ai/api/search-image?query=Confident%20latina%20woman%20late%20twenties%20sophisticated%20elegant%20style%20warm%20genuine%20smile%20diamond%20jewelry%20detail%20premium%20portrait%20photography%20soft%20warm%20neutral%20background%20subtle%20golden%20glow%20editorial%20quality&width=100&height=100&seq=rxn-user-isabella&orientation=squarish', contentId: 1, contentType: 'posts', reactionId: 'esta-brutal', timeAgo: 'Hace 10 min' },
  { id: 2, userId: 2, userName: 'Felipe Gutiérrez', userPhoto: 'https://readdy.ai/api/search-image?query=Charismatic%20latino%20man%20late%20twenties%20sharp%20casual%20blazer%20style%20confident%20warm%20expression%20premium%20portrait%20photography%20soft%20warm%20neutral%20background%20subtle%20golden%20glow%20editorial%20quality&width=100&height=100&seq=rxn-user-felipe&orientation=squarish', contentId: 1, contentType: 'posts', reactionId: 'me-encanta', timeAgo: 'Hace 15 min' },
  { id: 3, userId: 3, userName: 'Adriana Mejía', userPhoto: 'https://readdy.ai/api/search-image?query=Warm%20nostalgic%20latina%20woman%20late%20twenties%20elegant%20casual%20look%20artistic%20flair%20genuine%20gentle%20smile%20premium%20portrait%20photography%20soft%20warm%20neutral%20background%20subtle%20golden%20glow%20editorial%20quality&width=100&height=100&seq=rxn-user-adriana&orientation=squarish', contentId: 2, contentType: 'videos', reactionId: 'sorprendente', timeAgo: 'Hace 22 min' },
  { id: 4, userId: 4, userName: 'Javier Mendoza', userPhoto: 'https://readdy.ai/api/search-image?query=Sporty%20energetic%20latino%20man%20late%20twenties%20athletic%20casual%20style%20confident%20smile%20premium%20portrait%20photography%20soft%20warm%20neutral%20background%20subtle%20golden%20glow%20editorial%20quality&width=100&height=100&seq=rxn-user-javier&orientation=squarish', contentId: 2, contentType: 'videos', reactionId: 'esta-brutal', timeAgo: 'Hace 30 min' },
  { id: 5, userId: 5, userName: 'Camila Restrepo', userPhoto: 'https://readdy.ai/api/search-image?query=Friendly%20latina%20woman%20late%20twenties%20warm%20urban%20casual%20style%20bright%20smile%20premium%20portrait%20photography%20soft%20warm%20neutral%20background%20subtle%20golden%20glow%20editorial%20quality&width=100&height=100&seq=rxn-user-camila&orientation=squarish', contentId: 3, contentType: 'fotos', reactionId: 'excelente', timeAgo: 'Hace 45 min' },
  { id: 6, userId: 6, userName: 'Ricky Navarro', userPhoto: 'https://readdy.ai/api/search-image?query=Charismatic%20latino%20man%20late%20twenties%20radio%20host%20casual%20vibe%20warm%20smile%20premium%20portrait%20photography%20soft%20warm%20neutral%20background%20subtle%20golden%20glow%20editorial%20quality&width=100&height=100&seq=rxn-user-ricky&orientation=squarish', contentId: 4, contentType: 'programas', reactionId: 'gran-programa', timeAgo: 'Hace 1 hora' },
  { id: 7, userId: 7, userName: 'Valentina Ríos', userPhoto: 'https://readdy.ai/api/search-image?query=Trendy%20latina%20woman%20mid%20twenties%20fashionable%20streetwear%20style%20bright%20confident%20smile%20premium%20portrait%20photography%20soft%20warm%20neutral%20background%20subtle%20golden%20glow%20editorial%20quality&width=100&height=100&seq=rxn-user-valentina&orientation=squarish', contentId: 5, contentType: 'posts', reactionId: 'me-encanta', timeAgo: 'Hace 2 horas' },
  { id: 8, userId: 8, userName: 'Gabriel Paredes', userPhoto: 'https://readdy.ai/api/search-image?query=Intellectual%20latino%20man%20late%20twenties%20refined%20casual%20style%20glasses%20warm%20thoughtful%20expression%20premium%20portrait%20photography%20soft%20warm%20neutral%20background%20subtle%20golden%20glow%20editorial%20quality&width=100&height=100&seq=rxn-user-gabriel&orientation=squarish', contentId: 6, contentType: 'videos', reactionId: 'excelente', timeAgo: 'Hace 3 horas' },
  { id: 9, userId: 9, userName: 'Laura Santamaría', userPhoto: 'https://readdy.ai/api/search-image?query=Elegant%20latina%20woman%20mid%20twenties%20classic%20refined%20style%20warm%20genuine%20smile%20premium%20portrait%20photography%20soft%20warm%20neutral%20background%20subtle%20golden%20glow%20editorial%20quality&width=100&height=100&seq=rxn-user-laura&orientation=squarish', contentId: 7, contentType: 'eventos', reactionId: 'esta-brutal', timeAgo: 'Hace 4 horas' },
  { id: 10, userId: 10, userName: 'Daniel Ortega', userPhoto: 'https://readdy.ai/api/search-image?query=Relaxed%20latino%20man%20mid%20twenties%20casual%20tshirt%20style%20easygoing%20smile%20premium%20portrait%20photography%20soft%20warm%20neutral%20background%20subtle%20golden%20glow%20editorial%20quality&width=100&height=100&seq=rxn-user-daniel&orientation=squarish', contentId: 8, contentType: 'locutores', reactionId: 'excelente', timeAgo: 'Hace 5 horas' },
  { id: 11, userId: 11, userName: 'Paola Rincón', userPhoto: 'https://readdy.ai/api/search-image?query=Sporty%20casual%20latina%20woman%20mid%20twenties%20confident%20warm%20smile%20athletic%20streetwear%20style%20premium%20portrait%20photography%20soft%20warm%20neutral%20background%20subtle%20golden%20glow%20editorial%20quality&width=100&height=100&seq=rxn-user-paola&orientation=squarish', contentId: 1, contentType: 'posts', reactionId: 'excelente', timeAgo: 'Hace 6 horas' },
  { id: 12, userId: 12, userName: 'María José Salazar', userPhoto: 'https://readdy.ai/api/search-image?query=Bright%20friendly%20latina%20woman%20mid%20twenties%20trendy%20casual%20look%20warm%20open%20smile%20premium%20portrait%20photography%20soft%20warm%20neutral%20background%20subtle%20golden%20glow%20editorial%20quality&width=100&height=100&seq=rxn-user-majose&orientation=squarish', contentId: 9, contentType: 'concursos', reactionId: 'buena-musica', timeAgo: 'Hace 7 horas' },
  { id: 13, userId: 13, userName: 'Carlos Herrera', userPhoto: 'https://readdy.ai/api/search-image?query=Friendly%20latino%20man%20mid%20twenties%20clean%20casual%20style%20warm%20smile%20premium%20portrait%20photography%20soft%20warm%20neutral%20background%20subtle%20golden%20glow%20editorial%20quality&width=100&height=100&seq=rxn-user-carlos&orientation=squarish', contentId: 2, contentType: 'videos', reactionId: 'buena-musica', timeAgo: 'Hace 8 horas' },
  { id: 14, userId: 14, userName: 'Sofía Quintero', userPhoto: 'https://readdy.ai/api/search-image?query=Warm%20genuine%20latina%20woman%20mid%20twenties%20relaxed%20boho%20style%20soft%20natural%20smile%20premium%20portrait%20photography%20soft%20warm%20neutral%20background%20subtle%20golden%20glow%20editorial%20quality&width=100&height=100&seq=rxn-user-sofiaq&orientation=squarish', contentId: 3, contentType: 'fotos', reactionId: 'me-gusta', timeAgo: 'Hace 9 horas' },
  { id: 15, userId: 15, userName: 'Andrés Murillo', userPhoto: 'https://readdy.ai/api/search-image?query=Confident%20latino%20man%20mid%20twenties%20casual%20hoodie%20street%20style%20warm%20engaging%20smile%20premium%20portrait%20photography%20soft%20warm%20neutral%20background%20subtle%20golden%20glow%20editorial%20quality&width=100&height=100&seq=rxn-user-andres&orientation=squarish', contentId: 10, contentType: 'fotos', reactionId: 'esta-brutal', timeAgo: 'Hace 10 horas' },
];

export const reactionDashboardStats: ReactionDashboardStats = {
  totalReactions: 11945000,
  reactionsToday: 84700,
  reactionsThisWeek: 612000,
  reactionsThisMonth: 2840000,
  mainReaction: 'Me Gusta',
  mainReactionCount: 2847000,
  trendingReaction: 'Está Brutal',
  trendingReactionPercent: 12.3,
  totalScoreGenerated: 28450000,
  avgReactionsPerUser: 24.8,
  activeReactors: 482000,
};

export const scoreActions: ScoreAction[] = [
  { id: 'dar-reaccion', label: 'Dar Reacción', description: 'Cada vez que reaccionás a contenido', points: 1, icon: 'ri-thumb-up-line', color: 'text-sky-600' },
  { id: 'recibir-reaccion', label: 'Recibir Reacción', description: 'Alguien reacciona a tu contenido', points: 2, icon: 'ri-heart-3-line', color: 'text-rose-600' },
  { id: 'reaccion-premium', label: 'Reacción Premium', description: 'Usar 🔥 Está Brutal o 👏 Excelente', points: 3, icon: 'ri-fire-line', color: 'text-orange-600' },
  { id: 'tendencia', label: 'Contenido en Tendencia', description: 'Tu contenido llega al top 10 de reacciones del día', points: 25, icon: 'ri-line-chart-line', color: 'text-amber-600' },
  { id: 'racha-reacciones', label: 'Racha de Reacciones', description: 'Reaccionar 7 días seguidos', points: 50, icon: 'ri-flashlight-line', color: 'text-fuchsia-600' },
  { id: 'reaccion-embajador', label: 'Reacción de Embajador', description: 'Un Embajador reacciona a tu contenido', points: 15, icon: 'ri-vip-crown-line', color: 'text-amber-600' },
  { id: 'reaccion-vip', label: 'Reacción VIP', description: 'Un VIP reacciona a tu contenido', points: 10, icon: 'ri-vip-diamond-line', color: 'text-violet-600' },
];

export function getReactionById(id: ReactionId): ReactionType | undefined {
  return reactionTypes.find((r) => r.id === id);
}

export function getContentTypeById(id: ContentTypeId): ContentTypeConfig | undefined {
  return contentTypeConfigs.find((ct) => ct.id === id);
}

export function getReactionsForContentType(typeId: ContentTypeId): ReactionType[] {
  const config = contentTypeConfigs.find((ct) => ct.id === typeId);
  if (!config) return [];
  return config.applicableReactions.map((rid) => getReactionById(rid)).filter((r): r is ReactionType => r !== undefined);
}

export function getContentByType(typeId: ContentTypeId): ContentItem[] {
  return topReactedContent.filter((c) => c.type === typeId);
}

export function getTrendingContent(limit = 6): ContentItem[] {
  return topReactedContent.filter((c) => c.trending).slice(0, limit);
}

export function getTopReactions(limit = 3): ReactionType[] {
  return [...reactionTypes].sort((a, b) => b.totalCount - a.totalCount).slice(0, limit);
}

export function getTrendingReactions(limit = 3): ReactionType[] {
  return [...reactionTypes].sort((a, b) => b.trendPercent - a.trendPercent).slice(0, limit);
}

export function getRecentActivity(limit = 8): UserReaction[] {
  return [...userReactions].slice(0, limit);
}